import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { FloatingLabel } from './FloatingLabel';
import PropTypes from 'prop-types';
import { safeHtml } from '../utils/safeHtml';
import { Validator } from './ValidationResult';
import { makeLabel } from '../utils/generalUtils';
import { useAutofillNotifier } from '../hooks/autofillHook';
import { withComponentCapabilities } from './capabilities/componentCapabilities';

export const customCtrlDef = {
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                [
                    { type: "number", min: 1, step: 1, field: "rowspan", label: t('rows') },
                ],
                [
                    { type: "number", min: 1, step: 1, field: "maxlength", label: t('maxLength') }
                ],
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, style, href, ...props }) => <TextareaInput rows={def.rowspan} maxLength={def.maxlength} style={{ resize: def?.resize ?? 'none', ...style }} href={href ?? def.href} {...props}></TextareaInput>
}

/**
 * Multi-line textbox.
 * @component
 */
const TextareaInput = withComponentCapabilities(forwardRef(({value, live = false, onChange, placeholder, required = false, hideLabel = false, style, readonly = false, disabled = false, prependText, prependCtrl, appendText, appendCtrl, href, ...props}, ref) => {
    const [text, setText] = useState(value ?? '');
    const [validity, setValidity] = useState();
    const { createAutofillProps } = useAutofillNotifier();
    const ctrlRef = useRef();

    useEffect(() => {
        setText(value ?? '')
        setValidity();
    }, [value]);

    const handleUpdate = useCallback(newText => {
        const valid = ctrlRef.current.checkValidity();
        setValidity(valid);
        if (valid && (newText ?? '') !== (value ?? '')) {
            onChange(newText);
        }
    }, [onChange, value]);

    const handleChange = useCallback(e => {
        const newText = e.target.value;
        setText(newText);
        if (live || document.activeElement !== e.target) {
            handleUpdate(newText)
        }
    }, [handleUpdate, live]);

    const autofillProps = useMemo(() =>
        createAutofillProps((el) => {
            const newText = el.value;
            let shouldUpdate = false;

            setText(prev => {
                shouldUpdate = newText && prev !== newText;
                return newText;
            });

            if (shouldUpdate && (!required || newText)) {
                handleUpdate(newText);
                return true;
            }
            return shouldUpdate;
        }, ctrlRef)
    , [createAutofillProps, handleUpdate, required, value]);

    if (readonly) {
        return <FloatingLabel placeholder={placeholder} hideLabel={hideLabel} value={value} href={href}>
            {safeHtml(value?.replaceAll('\n', '<br>') ?? '')}
        </FloatingLabel>
    }

    if (prependCtrl && prependText) {
        throw new Error("Cannot specify both prependCtrl and prependText");
    }

    if (appendCtrl && appendText) {
        throw new Error("Cannot specify both appendCtrl and appendText");
    }

    let prepend = null;
    if (prependCtrl || prependText) {
        prepend = prependCtrl ?? <InputGroup.Text>{prependText}</InputGroup.Text>
    }

    let append = null;
    if (appendCtrl || appendText) {
        append = appendCtrl ?? <InputGroup.Text>{appendText}</InputGroup.Text>
    }

    const textInput = <Form.Control as="textarea" {...autofillProps} isInvalid={validity === false} placeholder={makeLabel(placeholder, required)} value={text} onChange={e => handleChange(e)} onBlur={() => handleUpdate(text)} style={{resize: style?.resize ?? 'none', ...style}} ref={node => { ctrlRef.current = node; if (ref) { ref.current = node } }} disabled={disabled} required={required} onInvalid={() => setValidity(false)} {...props}></Form.Control>;

    return  <FloatingLabel hideLabel={hideLabel} placeholder={placeholder} required={required} value={text} href={href}>
            <Validator value={value} ref={ctrlRef} onValidation={setValidity}>
                { prepend || append ? 
                <InputGroup>
                    {prepend ? <InputGroup.Prepend>{prepend}</InputGroup.Prepend> : null}
                    {textInput}
                    {append ? <InputGroup.Append>{append}</InputGroup.Append> : null}
                </InputGroup>
                : textInput }
            </Validator>
        </FloatingLabel>
}), {

});

TextareaInput.propTypes = {
    /**
     * The text to be displayed by the control
     */
    value: PropTypes.string,

    /**
     * Set to true for every key stroke to call the onChange function
     */
    live: PropTypes.bool,

    /**
     * Function to call when the value has been changed
     */
    onChange: PropTypes.func.isRequired,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,
    
    /**
     * The maximum number of characters that can be entered into this control
     */
    maxLength: PropTypes.number,

    /**
     * The number of rows of text that should be visible in the textarea without scrolling
     */
    rows: PropTypes.number,

    /**
     * Render the control value as text rather an input control
     */
    readonly: PropTypes.bool,

    /**
     * A CSS styling object to control the look and feel of the editor
     */
    style: PropTypes.object,

    /**
     * A control to prepend to the beginning of the text input. It is invalid to specify both prependCtrl and prependText
     */
    prependCtrl: PropTypes.node,

    /**
     * Text or icon to prepend to the beginning of the text input. It is invalid to specify both prependCtrl and prependText
     */
    prependText: PropTypes.node,

    /**
     * A control to append to the end of the text input. It is invalid to specify both appendCtrl and appendText
     */
    appendCtrl: PropTypes.node,

    /**
    * Text or icon to append to the end of the text input. It is invalid to specify both appendCtrl and appendText
    */
    appendText: PropTypes.node,
}

export default TextareaInput;